import React, { useState, useEffect } from 'react';
import lessonService from '../../services/teacher/lessonService';
import useAuth from '../../hooks/useAuth';

export default function EarningsPage() {
  const [earnings, setEarnings] = useState({
    summary: {
      toplam_ders: 0,
      toplam_hakedis: 0,
      bu_ay_hakedis: 0,
      gecen_ay_hakedis: 0,
      bu_ay_detay: null,
      gecen_ay_detay: null,
      toplam_detay: null
    },
    durationDistribution: [],
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const data = await lessonService.getEarningsSummary();
      setEarnings(data);
    } catch (error) {
      console.error('Hakediş bilgileri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Hakediş Özeti</h1>

      {/* Ana Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Toplam Ders</p>
              <p className="text-3xl font-bold">
                {earnings.summary.toplam_ders || 0}
              </p>
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Bu Ay</p>
              <p className="text-3xl font-bold">
                ₺{parseFloat(earnings.summary.bu_ay_hakedis || 0).toFixed(2)}
              </p>
              {earnings.summary.bu_ay_detay && parseFloat(earnings.summary.bu_ay_detay.avans) > 0 && (
                <div className="mt-2 text-xs opacity-75">
                  <p>Brüt: ₺{parseFloat(earnings.summary.bu_ay_detay.brut_toplam).toFixed(2)}</p>
                  <p>Avans: -₺{parseFloat(earnings.summary.bu_ay_detay.avans).toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Geçen Ay</p>
              <p className="text-3xl font-bold">
                ₺{parseFloat(earnings.summary.gecen_ay_hakedis || 0).toFixed(2)}
              </p>
              {earnings.summary.gecen_ay_detay && parseFloat(earnings.summary.gecen_ay_detay.avans) > 0 && (
                <div className="mt-2 text-xs opacity-75">
                  <p>Brüt: ₺{parseFloat(earnings.summary.gecen_ay_detay.brut_toplam).toFixed(2)}</p>
                  <p>Avans: -₺{parseFloat(earnings.summary.gecen_ay_detay.avans).toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">Toplam Hakediş</p>
              <p className="text-3xl font-bold">
                ₺{parseFloat(earnings.summary.toplam_hakedis || 0).toFixed(2)}
              </p>
              {earnings.summary.toplam_detay && parseFloat(earnings.summary.toplam_detay.avans) > 0 && (
                <div className="mt-2 text-xs opacity-75">
                  <p>Brüt: ₺{parseFloat(earnings.summary.toplam_detay.brut_toplam).toFixed(2)}</p>
                  <p>Avans: -₺{parseFloat(earnings.summary.toplam_detay.avans).toFixed(2)}</p>
                </div>
              )}
            </div>
            <div className="text-white opacity-80">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Ders Süresine Göre Dağılım */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Ders Süresine Göre Dağılım</h2>
          {earnings.durationDistribution.length > 0 ? (
            <div className="space-y-4">
              {earnings.durationDistribution.map((item) => (
                <div key={item.ders_sure} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600">{item.ders_sure}</span>
                      <span className="text-xs text-blue-600 ml-1">dk</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{item.adet} Ders</p>
                      <p className="text-sm text-gray-500">Toplam Süre: {item.ders_sure * item.adet} dakika</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-green-600">
                      ₺{parseFloat(item.toplam || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Henüz veri bulunmuyor</p>
          )}
        </div>

        {/* Aylık Trend */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Son 6 Aylık Trend</h2>
          {earnings.monthlyTrend.length > 0 ? (
            <div className="space-y-3">
              {earnings.monthlyTrend.map((item) => (
                <div key={item.ay} className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">{formatMonth(item.ay)}</p>
                      <p className="text-sm text-gray-500">{item.ders_sayisi} ders verildi</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-green-600">
                        ₺{parseFloat(item.net_ucret || item.toplam_ucret || 0).toFixed(2)}
                      </p>
                      {item.avans && parseFloat(item.avans) > 0 && (
                        <p className="text-xs text-red-500">
                          Avans: -₺{parseFloat(item.avans).toFixed(2)}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        Ortalama: ₺{((item.net_ucret || item.toplam_ucret) / item.ders_sayisi).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Henüz veri bulunmuyor</p>
          )}
        </div>
      </div>

      {/* Detaylı Bilgi Kartı */}
      <div className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Hakediş Hesaplama Bilgisi</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4">
            <p className="text-gray-600 mb-1">40 Dakikalık Ders</p>
            <p className="font-medium text-gray-900">1x Ücret Katsayısı</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-gray-600 mb-1">60 Dakikalık Ders</p>
            <p className="font-medium text-gray-900">1.5x Ücret Katsayısı</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-gray-600 mb-1">80 Dakikalık Ders</p>
            <p className="font-medium text-gray-900">2x Ücret Katsayısı</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          * Ders ücretleri, öğrencinin kurs paketine ve ders süresine göre otomatik olarak hesaplanmaktadır.
        </p>
      </div>
    </div>
  );
}